import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Check, ArrowRight, Building2, Laptop, Zap, HeartHandshake } from 'lucide-react';

const OutilGestionPage = () => {
  const features = [
    { title: "Coproprietaires", items: ["Annuaire complet", "Import EDD automatique", "Gestion des tantiemes", "Historique des mutations"] },
    { title: "Assemblees generales", items: ["Convocations legales", "Ordre du jour personnalise", "Feuille de presence", "PV automatique"] },
    { title: "Finances", items: ["Budget previsionnel", "Appels de fonds", "Suivi des paiements", "Regularisation"] },
  ];

  const problems = [
    { problem: "Calculs de charges complexes", solution: "Calcul automatique selon vos tantiemes" },
    { problem: "Convocations AG fastidieuses", solution: "Generation en 2 clics, conformes a la loi" },
    { problem: "PV d'AG interminables a rediger", solution: "Proces-verbal pre-rempli avec les votes" },
    { problem: "Documents eparilles", solution: "Tout centralise et accessible en ligne" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Outil de Gestion de Copropriete en Ligne | Logiciel Copro 2026</title>
        <meta name="description" content="Outil de gestion de copropriete en ligne complet. Logiciel copro pour syndics benevoles : assemblees generales, charges, comptabilite. Simple et efficace." />
        <link rel="canonical" href="https://copropriete.app/outil-gestion-copropriete" />
      </Helmet>

      <header className="bg-white border-b border-slate-200 py-4 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold flex items-center gap-2 text-slate-900">
            <Building2 className="w-6 h-6 text-indigo-600" />
            copropriete.app
          </Link>
          <Link to="/login" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            C'est gratuit
          </Link>
        </div>
      </header>

      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Nouveau : Import automatique EDD avec IA
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            L'outil de gestion de copropriete <span className="text-indigo-600">qui simplifie tout</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Un logiciel de copro en ligne pense pour les syndics benevoles. Gagnez du temps, evitez les erreurs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login" className="inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-8 py-3 rounded-lg font-medium">
              Tester gratuitement <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 bg-indigo-600">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div><div className="text-3xl font-bold">500+</div><div className="text-indigo-200 text-sm">Coproprietes gerees</div></div>
            <div><div className="text-3xl font-bold">10k+</div><div className="text-indigo-200 text-sm">Utilisateurs actifs</div></div>
            <div><div className="text-3xl font-bold">50k+</div><div className="text-indigo-200 text-sm">Documents generes</div></div>
            <div><div className="text-3xl font-bold">4.8/5</div><div className="text-indigo-200 text-sm">Satisfaction client</div></div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Fini les galeres de la gestion de copro</h2>
              <div className="space-y-4">
                {problems.map((item, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <span className="text-slate-400 line-through">{item.problem}</span>
                      <p className="text-slate-900 font-medium">{item.solution}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-100 rounded-2xl p-8 flex items-center justify-center">
              <Laptop className="w-48 h-48 text-slate-300" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Un outil de gestion copro complet</h2>
            <p className="text-slate-600">Tout ce dont vous avez besoin pour gerer votre copropriete comme un pro</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((category, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-slate-900 mb-4">{category.title}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-600 text-sm">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <HeartHandshake className="w-16 h-16 text-indigo-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">Rejoignez les syndics benevoles qui nous font confiance</h2>
          <p className="text-slate-400 mb-8 text-lg">C'est gratuit 14 jours - Aucune carte bancaire requise</p>
          <Link to="/login" className="inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-8 py-3 rounded-lg font-medium">
            Creer mon compte gratuit <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm">2026 copropriete.app - Outil de gestion de copropriete en ligne</p>
        </div>
      </footer>
    </div>
  );
};

export default OutilGestionPage;
